import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import validator from "validator";

/** A tenant user offered as an autocomplete suggestion. */
export interface EmailSuggestion {
    email: string;
    name?: string;
}

/** Imperative handle for committing text still in the input (e.g. on submit). */
export interface AcxEmailChipInputHandle {
    /**
     * Commits any valid address still typed in the input into the recipient
     * list and returns the resulting list. Invalid leftover text is kept in the
     * input so it is not silently lost.
     */
    flushPending: () => string[];
}

// Reuse the platform's email validation (validator lib, same as ValidationStore's "isEmail").
const isValidEmail = (value: string) => validator.isEmail(value);

const emailOf = (option: string | EmailSuggestion) =>
    typeof option === "string" ? option : option.email;

interface Props {
    /** Currently selected recipient emails. */
    values: string[];
    setValues: (values: string[]) => void;
    /** Tenant users offered as autocomplete suggestions. */
    suggestions?: EmailSuggestion[];
    loading?: boolean;
    placeholderText?: string;
    helperText?: string;
    /** Notifies the parent of the current (uncommitted) input text. */
    onPendingTextChange?: (text: string) => void;
}

/**
 * Email recipient picker built on MUI's Autocomplete: free-typed addresses become
 * chips (Enter or comma), and known tenant users are offered as suggestions showing
 * name + email. Entries are validated and de-duplicated (case-insensitive) before
 * they are committed.
 */
const AcxEmailChipInput = forwardRef<AcxEmailChipInputHandle, Props>(
    (
        {
            values,
            setValues,
            suggestions = [],
            loading,
            placeholderText,
            helperText,
            onPendingTextChange,
        },
        ref,
    ) => {
        const [inputValue, setInputValue] = useState("");

        const updateInput = (text: string) => {
            setInputValue(text);
            onPendingTextChange?.(text);
        };

        // Validate + de-duplicate (case-insensitive), commit, and return the list.
        const commit = (candidates: string[]): string[] => {
            const seen = new Set<string>();
            const cleaned: string[] = [];
            for (const raw of candidates) {
                const email = raw.trim();
                const key = email.toLowerCase();
                if (!isValidEmail(email) || seen.has(key)) continue;
                seen.add(key);
                cleaned.push(email);
            }
            setValues(cleaned);
            return cleaned;
        };

        useImperativeHandle(ref, () => ({
            flushPending: () => {
                const valid: string[] = [];
                const invalid: string[] = [];
                for (const token of inputValue.split(",")) {
                    const email = token.trim();
                    if (!email) continue;
                    (isValidEmail(email) ? valid : invalid).push(email);
                }
                const result = commit(values.concat(valid));
                // Keep anything invalid in the input so it is not lost.
                updateInput(invalid.join(", "));
                return result;
            },
        }));

        return (
            <Autocomplete
                multiple
                freeSolo
                autoHighlight
                handleHomeEndKeys
                options={suggestions}
                loading={loading}
                value={values}
                inputValue={inputValue}
                filterSelectedOptions
                getOptionLabel={emailOf}
                isOptionEqualToValue={(option, value) =>
                    emailOf(option).toLowerCase() ===
                    emailOf(value).toLowerCase()
                }
                onChange={(_, newValue) => {
                    commit(newValue.map(emailOf));
                    updateInput("");
                }}
                onInputChange={(_, newInput, reason) => {
                    if (reason === "reset") {
                        updateInput("");
                        return;
                    }
                    // Treat commas as delimiters: commit whole tokens, keep the trailing fragment.
                    if (newInput.includes(",")) {
                        const parts = newInput.split(",");
                        const remainder = parts.pop() ?? "";
                        commit(values.concat(parts));
                        updateInput(remainder.trimStart());
                    } else {
                        updateInput(newInput);
                    }
                }}
                renderTags={(tagValues, getTagProps) =>
                    tagValues.map((option, index) => {
                        const tagProps = getTagProps({ index });
                        return (
                            <Chip
                                {...tagProps}
                                size="small"
                                label={emailOf(option)}
                            />
                        );
                    })
                }
                renderOption={(props, option) => {
                    const suggestion =
                        typeof option === "string"
                            ? { email: option }
                            : option;
                    return (
                        <Box component="li" {...props} key={suggestion.email}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    lineHeight: 1.2,
                                }}
                            >
                                {suggestion.name && (
                                    <Typography variant="body2">
                                        {suggestion.name}
                                    </Typography>
                                )}
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {suggestion.email}
                                </Typography>
                            </Box>
                        </Box>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        placeholder={values.length ? undefined : placeholderText}
                        helperText={
                            helperText ??
                            "Type an address and press ↵ or comma, or pick a teammate."
                        }
                    />
                )}
            />
        );
    },
);

AcxEmailChipInput.displayName = "AcxEmailChipInput";

export default AcxEmailChipInput;
